import { safeUrl } from "./safeUrl";
import React, { useState } from "react";
import RestaurantLogo from "./RestaurantLogo";
import { landingStrings, LandingLang } from "./landingStrings";

interface LandingPreviewProps {
  logoUrl?: string;
  logoScale?: number;
  restaurantName?: string;
  headlineFr?: string;
  headlineEn?: string;
  descriptionFr?: string;
  descriptionEn?: string;
  buttonTextFr?: string;
  buttonTextEn?: string;
  bilingual?: boolean;
  buttonUrl?: string;
  privacyPolicyUrl?: string;
  optInEnabled?: boolean;
  previewMode?: boolean;
  sponsorName?: string;
  sponsorUrl?: string;
  sponsorDescriptionFr?: string;
  sponsorDescriptionEn?: string;
}

const LandingPreview: React.FC<LandingPreviewProps> = ({
  logoUrl,
  logoScale,
  restaurantName,
  headlineFr,
  headlineEn,
  descriptionFr,
  descriptionEn,
  buttonTextFr,
  buttonTextEn,
  bilingual = false,
  buttonUrl,
  privacyPolicyUrl,
  optInEnabled = true,
  previewMode = false,
  sponsorName,
  sponsorUrl,
  sponsorDescriptionFr,
  sponsorDescriptionEn,
}) => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // French first, matching the real guest page. An admin previewing a
  // single-language restaurant never sees the switch at all.
  const [lang, setLang] = useState<LandingLang>('fr');
  const s = landingStrings[lang];
  const headline = lang === 'fr' ? headlineFr : headlineEn;
  const description = lang === 'fr' ? descriptionFr : descriptionEn;
  const buttonText = lang === 'fr' ? buttonTextFr : buttonTextEn;
  const sponsorDescription = lang === 'fr' ? sponsorDescriptionFr : sponsorDescriptionEn;

  const handleOptIn = (e: React.FormEvent) => {
    e.preventDefault();
    if (previewMode) {
      setSubmitted(true);
    }
    // In real mode, the parent should provide a submit handler
  };

  return (
    <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6 flex flex-col items-center">
      {bilingual && (
        <div className="self-end flex gap-1 mb-2">
          {(['fr', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={`px-2 py-0.5 text-xs rounded border ${lang === l ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300'}`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      <RestaurantLogo
        logoUrl={logoUrl}
        logoScale={logoScale}
        alt="Restaurant Logo"
        sizeClassName="w-24 h-24"
        className="mb-3"
      />
      <h1 className="text-2xl font-bold mb-1 text-center">{restaurantName || "Our Restaurant"}</h1>
      <div className="mb-4 w-full text-center">
        <h2 className="text-lg font-semibold mb-1">{headline}</h2>
        <p className="text-gray-700">{description}</p>
        {sponsorName && (
          <>
            <hr className="w-full border-gray-200 my-4" />
            <div>
              <p className="text-xs text-gray-500">
                {s.sponsoredBy} <span className="font-medium text-gray-700">{sponsorName}</span>
              </p>
              {sponsorDescription && <p className="text-xs text-gray-500 mt-1">{sponsorDescription}</p>}
              {sponsorUrl && (
                <a
                  href={safeUrl(sponsorUrl)}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="inline-block mt-2 text-xs font-medium text-gray-600 border border-gray-300 rounded-full px-3 py-1 hover:bg-gray-50 hover:border-gray-400 transition"
                >
                  {s.visitSponsor}
                </a>
              )}
            </div>
          </>
        )}
      </div>
      {optInEnabled && (!submitted ? (
        <form onSubmit={handleOptIn} className="w-full mb-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{s.emailOptional}</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder={s.emailPlaceholder}
              disabled={previewMode}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{s.phoneOptional}</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder={s.phonePlaceholder}
              disabled={previewMode}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              id="consent"
              className="mr-2"
              required
              disabled={previewMode}
            />
            <label htmlFor="consent" className="text-xs text-gray-600">
              {s.consentPrefix} {restaurantName || "this restaurant"}. {s.consentSuffix} {" "}
              <a
                href={safeUrl(privacyPolicyUrl) || `/privacy?lang=${lang}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-teal-600"
              >
                {s.privacyPolicy}
              </a>.
            </label>
          </div>
          <button
            type="submit"
            className="w-full py-2 bg-teal-600 text-white rounded-md font-semibold hover:bg-teal-700 disabled:opacity-50"
            disabled={!consent || previewMode}
          >
            {s.signUp}
          </button>
        </form>
      ) : (
        <div className="mb-4 w-full text-center text-green-600 font-semibold">
          {previewMode ? s.thanksPreview : s.thanksReal}
        </div>
      ))}
      <a
        href={safeUrl(buttonUrl) || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full block py-3 mt-2 bg-green-600 text-white rounded-md text-lg font-bold text-center hover:bg-green-700"
      >
        {buttonText}
      </a>
      <div className="mt-6 text-xs text-gray-400 text-center">
        {s.privacyFooter}
      </div>
    </div>
  );
};

export default LandingPreview;
